/**
 * CORE-1669 (QA FAIL 14.10.2026) — keep a headset mic off the WebAudio graph.
 *
 * iPad + AirPods: listeners heard the speaker's voice «mechanical». Two room
 * features send the mic through Agora's page-wide AudioContext, pinned to
 * 48 kHz (CORE-844): the software AGC (CORE-1554 — the first `setVolume` makes
 * Agora swap the sent track for the graph's output) and RNNoise (CORE-1598 —
 * `pipe` to `processorDestination`). A Bluetooth headset mic in call mode runs
 * at 16 or 24 kHz, so its sound is resampled inside the graph on the way out.
 * Such a mic goes out the way it did before 01.10 — unprocessed.
 *
 * Agora never takes a track back off the graph, so a mic that is already on it
 * has to be re-opened as a new track to leave it.
 */
import { AGORA_WEBAUDIO_SAMPLE_RATE } from '../createAgoraClient';
import { looksLikeBluetoothMic } from '../../utils/micSwitchMode';

/** False for a headset mic or a capture rate other than the graph's — such a mic stays off WebAudio. */
export function micFitsWebAudioGraph(track: any): boolean {
    try {
        const mst = track?._originMediaStreamTrack ?? track?.getMediaStreamTrack?.();
        const rate = mst?.getSettings?.()?.sampleRate;
        if (typeof rate === 'number' && rate !== AGORA_WEBAUDIO_SAMPLE_RATE) return false;
        return !looksLikeBluetoothMic(track?.getTrackLabel?.() ?? mst?.label);
    } catch {
        return true;
    }
}

/** iPhone / iPad — including an iPad that presents itself as a Mac (touch gives it away). */
export function isAppleTouchDevice(userAgent: string | null | undefined, maxTouchPoints?: number | null): boolean {
    const ua = userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (maxTouchPoints ?? 0) > 1);
}

/**
 * iOS moves the live capture onto a headset the moment it connects — same
 * track, no event on it. If that track's sound already goes through the graph,
 * the mic has to be re-opened on the headset. Returns the headset to re-open
 * on, or null.
 */
export function headsetToReopenMicOn(input: {
    appleTouch: boolean;
    /** The live mic track's sound goes through WebAudio (AGC or denoiser on it). */
    onGraph: boolean;
    liveLabel: string | null | undefined;
    mics: { deviceId: string; label: string }[];
}): string | null {
    if (!input.appleTouch || !input.onGraph) return null;
    if (looksLikeBluetoothMic(input.liveLabel)) return null;
    return input.mics.find(d => looksLikeBluetoothMic(d.label))?.deviceId ?? null;
}
